<template>
  <div :class="classes">
    <label
      class="asd20-multiselect-input__label"
      aria-hidden="true"
      v-if="!hideLabel"
      :for="computedId"
      >{{ label }}</label
    >
    <slot name="prefix" />
    <Asd20Icon v-if="icon" :name="icon" :size="iconSize" />

    <Multiselect
      ref="multiselect"
      autocomplete="off"
      v-fix-autocomplete
      :value="value"
      :options="computedItems"
      :taggable="taggable"
      v-bind="$attrs"
      multiple
      :track-by="itemValue"
      :label="itemLabel"
      :id="computedId"
      @input="$emit('input', $event)"
      @tag="addTag"
    >
      <!-- Override the input using the input slot -->
      <template slot="input" slot-scope="{ search, inputEvents }">
        <input
          :value="search"
          v-on="inputEvents"
          autocomplete="off"
          class="multiselect__input"
          placeholder="Type to search"
        />
      </template>
      <template slot="tag" slot-scope="{ option, remove }">
        <span
          v-if="option"
          :key="option[itemValue]"
          :style="{ '--background-color': option.color || '' }"
          class="multiselect__tag"
        >
          <span v-text="option[itemLabel]" />
          <i
            aria-hidden="true"
            tabindex="1"
            class="multiselect__tag-icon"
            @keypress.enter.prevent="remove(option)"
            @mousedown.prevent="remove(option)"
          />
          <i
            tabindex="1"
            class="multiselect__tag-icon"
            @keypress.enter.prevent="remove(option)"
            @mousedown.prevent="remove(option)"
          />
        </span>
      </template>

      <template slot="option" slot-scope="{ option }">
        <div class="option__desc">
          <span class="option__title">{{ option[itemLabel] }}</span>
        </div>
      </template>
    </Multiselect>

    <!-- <div style="outline-style: solid" id="demo">
      <select
        v-bind:class="{ 'fix-height': multiple === 'true' }"
        v-model="selected"
        multiple="true"
      >
        <option disabled value="">Please Select</option>
        <option
          v-for="item in computedItems"
          :key="items"
          :value="item"
          :id="computedId"
          >{{ item }}</option
        >
      </select>
    </div> -->

    <div
      class="asd20-multiselect-input__help-text"
      v-if="helpText"
      v-html="helpText"
    />
    <div
      class="asd20-multiselect-input__validation-error"
      v-for="(v, index) in validationErrors"
      :key="index"
      v-html="v"
    />
  </div>
</template>

<script>
import inputComponentMixin from '../../../mixins/inputComponentMixin'
import Asd20Icon from '../../atoms/Asd20Icon'
import Multiselect from 'vue-multiselect'

export default {
  name: 'Asd20MultiselectInput',
  mixins: [inputComponentMixin],
  components: { Asd20Icon, Multiselect },
  data: () => ({
    selected: '',
    multiple: 'true',
  }),
  directives: {
    fixAutocomplete: {
      // Called when the directive is bound to the element
      inserted(el) {
        const input = el.querySelector('input.multiselect__input');
        if (input) {
          input.setAttribute('autocomplete', 'off');
        }
      },
      // Ensure it applies even after updates
      componentUpdated(el) {
        const input = el.querySelector('input.multiselect__input');
        if (input) {
          input.setAttribute('autocomplete', 'off');
        }
      },
    },
  },
  methods: {
    addTag(newTag) {
      const tag = {}
      tag[this.itemLabel] = newTag
      tag[this.itemValue] = newTag
      this.$refs.multiselect.options.push(tag)
      this.$refs.multiselect.value = this.$refs.multiselect.value || []
      this.$refs.multiselect.value.push(tag)
      this.$emit('input', this.$refs.multiselect.value)
    },
  },
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style lang="scss" scoped>
@import '../../../design/_variables.scss';
@import '../../../design/_mixins.scss';

$sizes: (
  xs: space(1),
  sm: space(1.5),
  md: space(2.25),
  lg: space(3),
  xl: space(4),
);

$input-border-width: 2px;
$input-border-color: var(--color__accent);
$input-background-color: var(--color__accent-t80);
$input-background-color-hover: var(--website-page__alternate-background-t40);
$input-accent-color: var(--color__accent);
$input-text-color: var(--website-card__reverse-background-color);

$input-reversed-border-color: var(--color__tertiary);
$input-reversed-background-color: var(--color__primary);
$input-reversed-background-color-hover: var(--color__primary-s20);
$input-reversed-accent-color: var(--color__accent);
$input-reversed-text-color: var(--color__accent-t100);

.asd20-multiselect-input {
  position: relative;
  color: $input-text-color;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin: 0 0 space(0.5) 0;

  .asd20-icon {
    margin-left: space(0.25);
  }

  // Icon colors
  use {
    --line-color: #{$input-text-color};
    --fill-color: #{$input-accent-color};
  }

  &__label {
    display: flex;
    align-items: center;
    color: currentColor;
    flex-basis: 100%;
  }

  &::v-deep .multiselect,
  &::v-deep .multiselect__input,
  &::v-deep .multiselect__single {
    font-family: var(--website-typography__font-family-headlines);
    font-size: 1rem;
  }
  &::v-deep .multiselect {
    width: auto;
    flex-grow: 1;
    // margin-bottom: space(1);
    color: var(--website-card__reverse-background-color);
  }
  &::v-deep .multiselect--disabled {
    background: #ededed;
    pointer-events: none;
    opacity: 0.6;
  }
  &::v-deep .multiselect__input,
  &::v-deep .multiselect__single {
    border-radius: 0;
    background: var(--website-page__background-color);
  }
  &::v-deep .multiselect__input::placeholder {
    color: var(--website-card__reverse-background-color);
    font-weight: normal;
    font-size: 0.875rem;
  }

  &::v-deep .multiselect__input:hover,
  &::v-deep .multiselect__single:hover {
    border-color: var(--color__accent);
  }
  &::v-deep .multiselect__input:focus,
  &::v-deep .multiselect__single:focus {
    border-color: var(--color__accent);
    @include asd20-focus;
  }
  &::v-deep .multiselect__tags {
    border-radius: 0;
    border: 2px solid var(--color__accent);
    background: var(--website-page__background-color);
  }

  &::v-deep .multiselect--active .multiselect__tags {
    box-shadow: 0 0 0 5px var(--color__accent-t50) inset;
  }

  &::v-deep .multiselect__tag {
    --background-color: var(--color__primary);
    color: var(--website-page__background-color);
    font-weight: normal;
    background: var(--background-color);
    border-radius: 0;
  }
  &::v-deep .multiselect__tag-icon {
    font-weight: bold;
    border-radius: 0;
  }
  &::v-deep .multiselect__tag-icon:after {
    content: '×';
    color: var(--color__accent-t90);
    font-size: 14px;
  }
  &::v-deep .multiselect__tag-icon:focus,
  &::v-deep .multiselect__tag-icon:hover {
    background: rgba(0, 0, 0, 0.125);
    @include asd20-focus;
  }
  &::v-deep .multiselect__tag-icon:focus:after,
  &::v-deep .multiselect__tag-icon:hover:after {
    color: white;
  }
  &::v-deep .multiselect__current {
    border-radius: 0;
    border: 1px solid var(--color__accent);
    cursor: pointer;
  }
  &::v-deep .multiselect__select:before {
    color: var(--color__accent);
    border-color: var(--color__accent) transparent transparent transparent;
  }
  &::v-deep .multiselect__placeholder {
    color: var(--website-card__reverse-background-color);
    font-weight: normal;
    font-size: 0.875rem;
  }

  &::v-deep .multiselect__option--highlight {
    background: var(--color__accent);
  }
  &::v-deep .multiselect__option--highlight:after {
    content: attr(data-select);
    background: var(--color__accent);
  }
  &::v-deep .multiselect__option--selected {
    background: var(--website-page__background-color);
    color: var(--color__accent-s20);
  }
  &::v-deep .multiselect__option--selected:after {
    content: attr(data-selected);
    color: silver;
  }
  &::v-deep .multiselect__option--selected.multiselect__option--highlight {
    background: var(--color__accent);
    color: var(--website-page__background-color);
  }
  &::v-deep
    .multiselect__option--selected.multiselect__option--highlight:after {
    background: var(--color__accent);
    color: var(--website-page__background-color);
  }
  &::v-deep .multiselect__option--group {
    background: #ededed;
    color: #35495e;
  }
  &::v-deep .multiselect__option--group.multiselect__option--highlight {
    background: #35495e;
    color: var(--website-page__background-color);
  }
  &::v-deep .multiselect__option--group.multiselect__option--highlight:after {
    background: #35495e;
  }
  &::v-deep
    .multiselect__option--group-selected.multiselect__option--highlight {
    background: var(--color__accent);
    color: var(--website-page__background-color);
  }
  &::v-deep
    .multiselect__option--group-selected.multiselect__option--highlight:after {
    background: var(--color__accent);
    content: attr(data-deselect);
    color: var(--website-page__background-color);
  }

  &__help-text {
    flex-basis: 100%;
    color: var(--website-card__reverse-background-color);
    font-size: 0.75rem;
    margin-top: space(0.125);
  }

  &__validation-error {
    flex-basis: 100%;
    color: darkred;
    font-size: 0.75rem;
    margin-top: space(0.125);
  }

  &--has-errors {
    label {
      color: darkred;
    }
    &::v-deep .multiselect__tags {
      border: 2px solid darkred;
    }
    &::v-deep .multiselect--active .multiselect__tags {
      box-shadow: 0 0 0 5px darkred inset;
    }
    &::v-deep .multiselect__select:before {
      color: darkred;
      // border-color: var(--color__danger) transparent transparent transparent;
    }
  }
}
</style>
